import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from 'axios';
import { useWorkspace } from '../../../context/WorkspaceContext';

//buttons
import { Modal } from '../../../components/Modal/Modal';
import Breadcrumbs from "../../../components/Tools/Breadcrumbs";
import DropdownButton from "../../../components/Tools/DropdownButton";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaTrash, FaSync } from "react-icons/fa";
import { LuRefreshCcw } from "react-icons/lu";
import { LoadingIndicator } from "../../../components/Tools/LoadingIndicator";
import { Button } from "../../../ui/button";

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const Socials = ({ value, refreshTrigger }) => {
  const [userSocials, setUserSocials] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hasRunOnceRef = useRef(false);
  const viewTrackedRef = useRef(false);
  const { activeWorkspace, idToken, user } = useWorkspace();

  // Memoize getUsersSocials function
  const getUsersSocials = useCallback(async () => {
    if (!activeWorkspace || !idToken) return;
    setLoading(true);
    try {
      const response = await axios.get(BACKEND_API_URL + '/api/auth/user/social_media_tokens', {
        params: { workspaceId: activeWorkspace.id },
        headers: { Authorization: `Bearer ${idToken}` },
      });
      setUserSocials(response.data || []);
    } catch (error) {
      console.error(error);
      setUserSocials([]);
    } finally {
      setLoading(false);
    }
  }, [idToken, activeWorkspace]);

  const closeModal = () => {
    setOpenModal(false);
  };
  const openModalHandler = () => {
    setOpenModal(true);
  };

  // Listen for localStorage changes from other tabs
  useEffect(() => {
    // Function to check if social auth was successful
    const checkAuthSuccess = () => {
      const authSuccess = localStorage.getItem('social_auth_success');
      if (authSuccess) {
        // Clear the flag
        localStorage.removeItem('social_auth_success');
        // Refresh the socials list
        getUsersSocials();
      }
    };

    // Function to handle focus events for pending auth
    const handleFocus = () => {
      const pendingAuth = localStorage.getItem('social_auth_pending');
      if (pendingAuth) {
        // When the user returns to this tab, check if there are pending auths and refresh
        getUsersSocials();
        localStorage.removeItem('social_auth_pending');
      }
    };

    // Check immediately (for when returning to an already-open tab)
    checkAuthSuccess();

    // Set up event listener for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'social_auth_success') {
        checkAuthSuccess();
      }
    };

    // Listen for when the window regains focus
    window.addEventListener('focus', handleFocus);
    window.addEventListener('storage', handleStorageChange);
    
    // Clean up the event listeners
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [getUsersSocials]);

  useEffect(() => {
    if (!hasRunOnceRef.current && idToken) {
      hasRunOnceRef.current = true;
      getUsersSocials();
    }
  }, [idToken, getUsersSocials]);

  useEffect(() => {
    if (activeWorkspace && idToken) {
      getUsersSocials();
    }
  }, [activeWorkspace, idToken, getUsersSocials]);

  // Listen for external refresh triggers
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      getUsersSocials();
    }
  }, [refreshTrigger, getUsersSocials]);

  return (
    <div className={`${value ?? 'dashboard-container'}`}>
      {!value && (
        <div className="mb-3 flex items-center justify-between">
          <div>
            <Breadcrumbs />
            <p className="text-sm italic">Connect to your social medias to start uploading videos...</p>
          </div>
          <Button 
            variant="outline"
            size="sm"
            onClick={() => {
              setIsRefreshing(true);
              getUsersSocials().finally(() => setIsRefreshing(false));
            }}
            disabled={isRefreshing}
          >
            <FaSync className={`mr-1 ${isRefreshing ? 'animate-spin' : ''}`} /> 
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      )}

      {loading && !value ? (
        <div className="flex justify-center items-center h-screen">
          <LoadingIndicator showLogo={true} />
        </div>
      ) : (
        <ShowLoggedInSocials userSocials={userSocials} openModalHandler={openModalHandler} setUserSocials={setUserSocials} />
      )}

      {openModal && createPortal(
        <Modal open={openModal} closeModal={closeModal} type={'ConnectSocialsModal'} otherInfo={'hashtag'} />,
        document.body
      )}
    </div>
  );
}

const ShowLoggedInSocials = ({ userSocials, openModalHandler, setUserSocials }) => {
  const { activeWorkspace, idToken } = useWorkspace();

  const onDelete = async (id) => {
    if (!idToken) return;
    try {
      const response = await axios.delete(BACKEND_API_URL + '/api/auth/user/social/logout/' + id, {
        params: { workspaceId: activeWorkspace.id },
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const newUserSocials = userSocials.filter((social) => social.platform_id !== id);
      setUserSocials(newUserSocials);
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  const handleImageError = (event, platform) => {
    event.target.src = `../socials/${platform}-logo.svg`;
  };

  const handleReauthorize = async (platform) => {
    if (!idToken) return;
    if (platform === 'youtube') platform = 'google';

    try {
      const response = await axios.get(`${BACKEND_API_URL}/api/${platform}/oauth`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      window.open(response.data, '_blank');
    } catch (error) {
      console.error('Reauthorization error:', error);
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-4 w-full">
      <div className='md:hidden min-h-[200px] rounded-lg p-5 text-center relative --theme-colors border-dashed border-4 border-gray-300 flex items-center justify-center transition-all duration-300 hover:border-solid cursor-pointer' onClick={openModalHandler}>
        Connect social
        <br />
        +
      </div>
      {userSocials && userSocials.length > 0 ? (
        <>
          {userSocials.map((social, index) => (
            <div key={index} className='rounded-lg p-3 text-center relative --theme-colors --social-cards-shadow'>
              <div className="flex w-full justify-between items-center">
                <img src={`../socials/${social.platform}-logo.svg`} alt={social.platform} className="w-8"></img>

                <DropdownButton label={<BsThreeDotsVertical size={20} />} options={[
                  { label: 'Refresh account', onClick: () => handleReauthorize(social.platform), icon: <LuRefreshCcw className="text-blue-600 font-bold" /> },
                  { label: 'Disconnect account', onClick: () => onDelete(social.platform_id), color: 'red', icon: <FaTrash /> },
                  { extra: 'Expires: ' + new Date(social.expiresAt).toLocaleString().replace(/:\d{2}$/, '') }
                ]} />
              </div>

              <div className={`avatar-container max-w-[100px] w-[80%] mx-auto ${social.platform === 'tiktok' && 'rounded-full p-4 bg-black'}`}>
                <img 
                  className="avatar-image w-full h-auto rounded-full mx-auto mb-2" 
                  src={social.logo ? social.logo : `../socials/${social.platform}-logo.svg`} 
                  alt={social.username} 
                  onError={(e) => handleImageError(e, social.platform)} 
                />
              </div>
              <p className="text-lg font-semibold overflow-hidden text-ellipsis whitespace-nowrap">{social.username}</p>
            </div>
          ))}
          <div className='hidden md:flex min-h-[200px] rounded-lg p-5 text-center relative --theme-colors border-dashed border-4 border-gray-300 items-center justify-center transition-all duration-300 hover:border-solid cursor-pointer' onClick={openModalHandler}>
            Connect social
            <br />
            +
          </div>
        </>
      ) : (
        <div className='hidden md:flex min-h-[200px] rounded-lg p-5 text-center relative --theme-colors border-dashed border-4 border-gray-300 items-center justify-center transition-all duration-300 hover:border-solid cursor-pointer' onClick={openModalHandler}>
          Connect social
          <br />
          +
        </div>
      )}
    </div>
  );
};

export default Socials;